import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { DataPointsApiConfiguration } from 'src/app/api/proxy/datapoints/data-points-api-configuration';
import { consumerObservationGet, consumerStatisticsGet, consumerDetectSkinConditionPost } from 'src/app/api/proxy/datapoints/functions';
import { MemberStatistics } from 'src/app/api/proxy/datapoints/models/member-statistics';
import { CurrentObservationStatusModel } from 'src/app/api/proxy/datapoints/models/current-observation-status-model';
import { EventService } from '../events/event.service';
import { SecureStorageService } from '../storage/secure-storage.service';
import { StorageKey } from '../storage/models/storage-key.enum';
import { Network } from '@capacitor/network';
import { PromptService } from '../promtp-service/prompt.service';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { HealthScoreResponseModel, ObservationType } from 'src/app/api/proxy/datapoints/datapoints-models';
import { RangeLevel } from 'src/app/api/proxy/datapoints/models/range-level';
import { SkinConditionDetectResponseModel } from 'src/app/api/proxy/datapoints/models/skin-condition-detect-response-model';
import { HealthScoreService } from '../health-score-service/health-score.service';
import { LoadingService } from '../loading-service/loading.service';
import { QuestionnaireService } from '../questionnaire-service/questionnaire.service';

export const GAUGE_COLORS = {
    [RangeLevel.Poor]: {
        disabled: 'var(--viva-color-poor-disabled)',
        enabled: 'var(--viva-color-poor)'
    },
    [RangeLevel.Fair]: {
        disabled: 'var(--viva-color-fair-disabled)',
        enabled: 'var(--viva-color-fair)'
    },
    [RangeLevel.Average]: {
        disabled: 'var(--viva-color-average-disabled)',
        enabled: 'var(--viva-color-average)'
    },
    [RangeLevel.Good]: {
        disabled: 'var(--viva-color-good-disabled)',
        enabled: 'var(--viva-color-good)'
    },
    [RangeLevel.Excellent]: {
        disabled: 'var(--viva-color-excellent-disabled)',
        enabled: 'var(--viva-color-excellent)'
    },
};

@Injectable({
    providedIn: 'root'
})
export class BiometricService {

    private NETWORK_TIMEOUT = 40000;
    private SKIN_CHECK_NETWORK_TIMEOUT = 90000;
    private silentRefreshDebounceTimer: any = null;
    private readonly SILENT_REFRESH_DEBOUNCE_MS = 30000; // 30 seconds

    constructor(
        private http: HttpClient,
        private dataPointsCfg: DataPointsApiConfiguration,
        private _PromptService: PromptService,
        private myProfileService: MyProfileService,
        protected _HealthScoreService: HealthScoreService,
        private _questionnaireService: QuestionnaireService,
    ) {
        this.initializeEventListeners();
    }
    private _currentObservationStatus: CurrentObservationStatusModel | null = null;

    /**
     * Initialize event listeners for silent notification handling
     */
    private initializeEventListeners(): void {
        // Listen for biometric refresh events from silent notifications
        EventService.biometricRefreshRequired.subscribe(async () => {
            // Clear any existing debounce timer
            if (this.silentRefreshDebounceTimer) {
                clearTimeout(this.silentRefreshDebounceTimer);
                this.silentRefreshDebounceTimer = null;
            }
            
            // Set new debounce timer
            this.silentRefreshDebounceTimer = setTimeout(async () => {
                try {
                    // Enable silent loading to prevent UI loading animations
                    LoadingService.enableSilentLoading();
                    
                    try {
                        await this.getSummary(true); // Force refresh
                    } finally {
                        // Always disable silent loading after operation completes
                        LoadingService.disableSilentLoading();
                        this.silentRefreshDebounceTimer = null;
                    }
                } catch (error) {
                    // Silently handle errors - this is low priority processing
                    console.error('SILENT: Error refreshing biometric data from silent notification:', error);
                    this.silentRefreshDebounceTimer = null;
                }
            }, this.SILENT_REFRESH_DEBOUNCE_MS);
        });
    }


    public async getSummaryStatic(): Promise<CurrentObservationStatusModel | null> {
        const mySummary = await SecureStorageService.get(StorageKey.BiometricList);
        let model: CurrentObservationStatusModel | null = null;
        if (mySummary) {
            try {
                model = JSON.parse(mySummary);
            } catch {
                model = null;
            }
        }

        if (model) {
            this._currentObservationStatus = model;
            EventService.onBiometricListUpdated.emit(model);
        }
        return model;
    }

    public async getStatisticsStatic(): Promise<MemberStatistics[] | null> {
        const myStatistics = await SecureStorageService.get(StorageKey.BiometricStatisticsSummary);
        let model: MemberStatistics[] | null = null;
        if (myStatistics) {
            try {
                model = JSON.parse(myStatistics);
            } catch {
                model = null;
            }
        }
        return model;
    }

    public async getSummary(forced: boolean = false): Promise<CurrentObservationStatusModel | null> {
        let cacheTimestamp: Date | null = null;
        let model = await this.getSummaryStatic();

        if (model) {
            const ts = (model as any).fetchedAt;
            if (ts) {
                cacheTimestamp = new Date(ts);
            }
        }

        // Check if we need to refresh based on cache age
        if (model && cacheTimestamp && !forced) {
            const now = new Date();
            const ageHours = (now.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

            // Refresh if data is older than 2 hours
            if (ageHours < 2) {
                return model; // Cache is still fresh
            }
            // Otherwise, continue to fetch fresh data
        }

        const status = await Network.getStatus();
        if (!status.connected) {
            return model;
        }

        try {
            // Fetch observations and return raw model
            const httpResponse = await firstValueFrom(consumerObservationGet(this.http, this.dataPointsCfg.rootUrl).pipe(timeout(this.NETWORK_TIMEOUT)));
            const obs = httpResponse.body!;

            this._currentObservationStatus = obs;

            if(obs.healthScore){
                await this._HealthScoreService.healthScoreReceived(JSON.stringify(obs.healthScore));
            }else{
                await this._HealthScoreService.GetLastScore(true);
            }

            // Trigger statistics refresh
            await this.getStatistics();

            // Sync weight and height to profile if present
            await this.syncWeightToProfile(obs);
            await this.syncHeightToProfile(obs);

            // Add cache timestamp and persist/emit
            (obs as any).fetchedAt = new Date().toISOString();
            await SecureStorageService.set(StorageKey.BiometricList, JSON.stringify(obs));
            EventService.onBiometricListUpdated.emit(obs);
            this._questionnaireService.refreshAfterObservationsReceived();

            return obs;

        } catch (error) {
            await this._PromptService.showNetworkConnectionError(error, 'biometric summary');
            return model;
        }
    }

    private async getStatistics(): Promise<MemberStatistics[] | null> {
        const mySummary = await SecureStorageService.get(StorageKey.BiometricStatisticsSummary);

        let cacheTimestamp: Date | null = null;
        let model: MemberStatistics[] | null = null;

        if (mySummary) {
            try {
                model = JSON.parse(mySummary);
            } catch {
                model = null;
            }
        }

        if (model) {
            const ts = (model as any).fetchedAt;
            if (ts) {
                cacheTimestamp = new Date(ts);
            }
        }

        const status = await Network.getStatus();
        if (!status.connected) {
            return model;
        }

        try {
            const httpResponse = await firstValueFrom(consumerStatisticsGet(this.http, this.dataPointsCfg.rootUrl).pipe(timeout(this.NETWORK_TIMEOUT)));
            model = httpResponse.body!;
            (model as any).fetchedAt = new Date().toISOString();
            await SecureStorageService.set(StorageKey.BiometricStatisticsSummary, JSON.stringify(model));
        } catch (error) {
            await this._PromptService.showNetworkConnectionError(error, 'biometric statistics');
        }

        return model;
    }

    public async GetItems(): Promise<CurrentObservationStatusModel | null> {
        if (!this._currentObservationStatus) {
            const itms = await SecureStorageService.get(StorageKey.BiometricList);

            let model: CurrentObservationStatusModel | null = null;
            if (itms) {
                try {
                    model = JSON.parse(itms);

                } catch {
                    model = null;
                }
            }
            if (model) {
                this._currentObservationStatus = model;
            }
        }
        return this._currentObservationStatus;
    }

    public async getGuageStatistics(observationType: string): Promise<MemberStatistics | null> {
        if (!observationType) return null;

        const stats = await this.getStatisticsStatic();
        if (!stats) return null;

        // Find the statistics for this specific observation type
        const typeStat = stats.find(s => s.observationType === observationType);
        return typeStat ?? null;
    }

    /**
     * Sends a base64-encoded skin image to the datapoints API for analysis.
     * Server expects raw base64 (no data URL prefix).
     */
    public async detectSkinCondition(imageBase64: string): Promise<SkinConditionDetectResponseModel | null> {
        const status = await Network.getStatus();
        if (!status.connected) {
            await this._PromptService.showNetworkConnectionError(null, 'skin condition detection');
            return null;
        }
        const base64Only = imageBase64.replace(/^data:image\/\w+;base64,/, '');
        LoadingService.addSilentUrl(consumerDetectSkinConditionPost.PATH);
        try {
            const httpResponse = await firstValueFrom(
                consumerDetectSkinConditionPost(this.http, this.dataPointsCfg.rootUrl, {
                    body: { imageBase64: base64Only }
                }).pipe(timeout(this.SKIN_CHECK_NETWORK_TIMEOUT))
            );
            return httpResponse.body ?? null;
        } catch (error) {
            await this._PromptService.showNetworkConnectionError(error, 'skin condition detection');
            return null;
        } finally {
            LoadingService.removeSilentUrl(consumerDetectSkinConditionPost.PATH);
        }
    }

    private async syncWeightToProfile(observations: CurrentObservationStatusModel): Promise<void> {
        if (!observations?.observations || observations.observations.length === 0) {
            return;
        }

        // Find weight observation (check common weight observation types)
        const weightObs = observations.observations.find(obs =>
            obs.observationType === ObservationType.WeightInKgInstant
        );

        if (!weightObs || !weightObs.value) {
            return; // No weight observation found
        }

        try {
            // Get current profile
            const profile = await this.myProfileService.GetProfile(false);
            if (!profile) {
                return; // No profile found
            }

            // Convert weight to kg if needed (assuming observation value is in kg)
            const newWeightKg = weightObs.value;
            const currentWeightKg = profile.weightInKg ? profile.weightInKg : null;
            // Only update if weight is different and valid
            if (newWeightKg > 0 && newWeightKg !== currentWeightKg) {
                profile.weightInKg = newWeightKg;

                // Save updated profile to server
                const success = await this.myProfileService.SaveProfile(profile);

                if (success) {
                    // Weight synced to profile successfully
                }
            }
        } catch (error) {
            // Silently fail - don't interrupt the main flow
            console.error('Failed to sync weight to profile:', error);
        }
    }

    private async syncHeightToProfile(observations: CurrentObservationStatusModel): Promise<void> {
        if (!observations?.observations || observations.observations.length === 0) {
            return;
        }

        // Find height observation
        const heightObs = observations.observations.find(obs =>
            obs.observationType === ObservationType.HeightInCmInstant
        );

        if (!heightObs || !heightObs.value) {
            return; // No height observation found
        }

        try {
            // Get current profile
            const profile = await this.myProfileService.GetProfile(false);
            if (!profile) {
                return; // No profile found
            }

            // Height is already in cm from the observation
            const newHeightCm = heightObs.value;
            const currentHeightCm = profile.heightInCm ? profile.heightInCm : null;
            
            // Only update if height is different and valid (reasonable height range: 30-300 cm)
            if (newHeightCm > 0 && newHeightCm >= 30 && newHeightCm <= 300 && newHeightCm !== currentHeightCm) {
                profile.heightInCm = newHeightCm;

                // Save updated profile to server
                const success = await this.myProfileService.SaveProfile(profile);

                if (success) {
                    // Height synced to profile successfully
                }
            }
        } catch (error) {
            // Silently fail - don't interrupt the main flow
            console.error('Failed to sync height to profile:', error);
        }
    }

    /**
     * Check if an observation type represents a time value in seconds
     */
    public static isSecondsObservationType(observationType: string | null | undefined): boolean {
        if (!observationType) {
            return false;
        }

        return observationType === ObservationType.SleepDurationAggregate ||
            observationType === ObservationType.SleepTimeInBedSecondsAggregate ||
            observationType === ObservationType.SleepCoreSleepSecondsAggregate ||
            observationType === ObservationType.SleepRemSleepSecondsAggregate ||
            observationType === ObservationType.SleepDeepSleepSecondsAggregate ||
            observationType === ObservationType.SleepTimeAwakeDuringSleepSecondsAggregate ||
            observationType === ObservationType.ActivityDurationSecondsInstant ||
            observationType === ObservationType.ActivityRestSecondsInstant ||
            observationType === ObservationType.ActivityActiveSecondsInstant ||
            observationType === ObservationType.DailyTotalActiveTimeSecondsAggregate;
    }

    /**
     * Format seconds to time string (e.g., "8h 30m")
     */
    public static formatSecondsToTime(value: number): string {
        const hours = Math.floor(value / 60 / 60);
        const minutes = Math.floor((value % 3600) / 60);

        if (hours > 0 && minutes > 0)
            return `${hours}h ${minutes}m`;
        if (hours > 0)
            return `${hours}h`;
        else
            return `${minutes}m`;
    }

    /** Clears in-memory biometric summary without triggering a network fetch. */
    public clearCachedSummary(): void {
        this._currentObservationStatus = null;
    }

    /**
     * Reset the cache for biometric data to force a refresh on next request
     * This method clears the fetchedAt timestamp to make cached data appear stale
     * If the app is currently running, it will immediately fetch fresh data and update the UI
     */
    public async resetCache(): Promise<void> {
        try {
            
            // Get current cached data
            const cachedData = await SecureStorageService.get(StorageKey.BiometricList);
            
            if (cachedData) {
                try {
                    const model = JSON.parse(cachedData);
                    
                    // Set fetchedAt to a very old date to force refresh
                    model.fetchedAt = new Date('2020-01-01').toISOString();
                    
                    // Save back to storage with old timestamp
                    await SecureStorageService.set(StorageKey.BiometricList, JSON.stringify(model));
                } catch (parseError) {
                    // If parsing fails, just remove the cache entirely
                    await SecureStorageService.remove(StorageKey.BiometricList);
                }
            } else {
            }
            
            // Also reset statistics cache
            const cachedStats = await SecureStorageService.get(StorageKey.BiometricStatisticsSummary);
            if (cachedStats) {
                try {
                    const statsModel = JSON.parse(cachedStats);
                    statsModel.fetchedAt = new Date('2020-01-01').toISOString();
                    await SecureStorageService.set(StorageKey.BiometricStatisticsSummary, JSON.stringify(statsModel));
                } catch (parseError) {
                    await SecureStorageService.remove(StorageKey.BiometricStatisticsSummary);
                }
            } else {
            }
            
            // Clear in-memory cache
            this._currentObservationStatus = null;
            
            // If the app is currently running (has active observation status), 
            // immediately fetch fresh data and update the UI
            try {
                // Check if we have any cached data to determine if app is running
                if (cachedData || this._currentObservationStatus) {
                    
                    // Enable silent loading to prevent UI loading animations
                    LoadingService.enableSilentLoading();
                    
                    try {
                        // Fetch fresh data with forced refresh
                        await this.getSummary(true);
                    } finally {
                        // Always disable silent loading after operation completes
                        LoadingService.disableSilentLoading();
                    }
                } else {
                }
            } catch (fetchError) {
                console.error('SILENT: Error during immediate fetch:', fetchError);
                // Silently handle fetch errors - this is low priority processing
            }
            
        } catch (error) {
            console.error('SILENT: Error in resetCache:', error);
            // Silently handle errors - this is low priority processing
        }
    }

    /**
     * Format observation value based on observation type
     * This method provides consistent formatting across charts, labels, and ranges
     * @param value - The numeric value to format
     * @param observationType - The observation type to determine formatting rules
     * @returns Formatted string representation of the value
     */
    public static formatObservationValue(
        value: number | null | undefined,
        observationType: string | null | undefined
    ): string {
        if (value == null || value == undefined) {
            return '';
        }

        // Check if this is a seconds-based observation type
        if (BiometricService.isSecondsObservationType(observationType)) {
            return BiometricService.formatSecondsToTime(value);
        }

        // Always apply k-suffix formatting for large numbers (Steps, Calories, etc.)
        if (value >= 1000) {
            const roundedVal = Math.round((value / 1000) * 100) / 100;
            return roundedVal === Math.round(roundedVal)
                ? Math.round(roundedVal).toString() + "k"
                : roundedVal.toString() + "k";
        }

        if (!observationType) {
            // Default formatting for smaller numbers
            return Math.round(value).toString();
        }

        switch (observationType as ObservationType) {
            case ObservationType.DailyTotalCaloriesExpenditureAggregate:
            case ObservationType.DailyTotalCaloriesActiveAggregate:
            case ObservationType.DailyTotalCaloriesBmrAggregate:
            case ObservationType.DailyTotalStepsAggregate:
            case ObservationType.DailyTotalDistanceMetersAggregate:
            case ObservationType.DailyTotalDistanceFloorsAggregate:
            case ObservationType.ActivityTotalEnergyKcalInstant:
            case ObservationType.ActivityTotalDistanceMetersInstant:
            case ObservationType.ActivityDistanceStepsInstant:
                // For these types, k-suffix would have already been applied above for >= 1000
                return Math.round(value).toString();

            case ObservationType.HeartRateInstant:
            case ObservationType.DailyHeartRateMaxAggregate:
            case ObservationType.DailyHeartRateMinAggregate:
            case ObservationType.DailyAverageHeartRateAggregate:
            case ObservationType.DailyAverageHeartRateRestingAggregate:
            case ObservationType.DailyAverageHeartRateRestingMinAggregate:
            case ObservationType.DailyAverageHeartRateRestingMaxAggregate:
            case ObservationType.ActivityAverageHeartRateInstant:
            case ObservationType.ActivityMinHeartRateInstant:
            case ObservationType.ActivityMaxHeartRateInstant:
            case ObservationType.BloodOxygenLevelSpo2Instant:
            case ObservationType.DailyAverageOxygenationSaturationsAggregate:
            case ObservationType.HeartStressIndexInstant:
            case ObservationType.MentalStressIndexInstant:
            case ObservationType.Vo2MaxInstant:
            case ObservationType.DailyAverageVo2MaxAggregate:
            case ObservationType.RespiratoryRateInstant:
            case ObservationType.HeightInCmInstant:
                // Round to whole numbers for these metrics
                return Math.round(value).toString();

            default:
                // Default case: round to max 2 decimal places
                const roundedVal = Math.round(value * 100) / 100;
                return roundedVal === Math.round(roundedVal)
                    ? Math.round(roundedVal).toString()
                    : roundedVal.toString();
        }
    }

    
}

